<template>
  <main class="login-page">
    <div class="login-wrapper">
      <!-- Left Panel -->
      <div class="login-panel login-panel-left">
        <div class="login-mesh-bg"></div>
        <div class="login-particles">
          <span v-for="n in 12" :key="n" :class="`particle particle-${n}`"></span>
        </div>
        <div class="login-logo-left">
          <img src="/assets/Logo.png" alt="MiStock" />
        </div>
        <div class="login-panel-left-content">
          <div class="login-illustration">
            <transition-group name="fade" tag="div" class="illustration-carousel">
              <div
                v-for="(image, index) in carouselImages"
                :key="index"
                v-show="currentImage === index"
                class="illustration-slide"
              >
                <img :src="image.src" :alt="image.alt" />
              </div>
            </transition-group>
          </div>

          <div class="login-carousel-dots">
            <span
              v-for="(image, index) in carouselImages"
              :key="index"
              @click="currentImage = index"
              :class="{ active: currentImage === index }"
            ></span>
          </div>

          <div class="login-tagline">
            <p>Administra tu inventario con precisión y en tiempo real.</p>
          </div>
        </div>
      </div>

      <!-- Right Panel -->
      <div class="login-panel login-panel-right">
        <div class="login-form-card">
          <div class="login-form-card-glow"></div>
          <div class="login-form-wrapper">
            <div class="login-form-header">
              <h1 class="login-title">Bienvenido</h1>
              <p class="login-subtitle">Ingresa tus credenciales para acceder al sistema</p>
            </div>

            <form role="form" @submit.prevent="handleLogin()">
              <div class="mb-3">
                <label class="login-label">Correo electrónico</label>
                <argon-input
                  v-model="user.email"
                  type="email"
                  class="mb-0"
                  placeholder="nombre@empresa.com"
                  name="email"
                  size="lg"
                  icon="fas fa-envelope"
                  iconDir="left"
                  :value="user.email"
                />
                <validation-error :errors="apiValidationErrors.email" />
              </div>

              <div class="mb-3">
                <label class="login-label">Contraseña</label>
                <argon-input
                  v-model="user.password"
                  type="password"
                  placeholder="••••••••"
                  name="password"
                  size="lg"
                  icon="fas fa-lock"
                  iconDir="left"
                  :value="user.password"
                />
              </div>

              <div class="login-options">
                <argon-switch id="rememberMe" inputClass="login-switch-input" labelClass="login-switch-label">
                  Recuérdame
                </argon-switch>
                <router-link
                  :to="{ name: 'Forgot Password' }"
                  class="login-forgot"
                >
                  ¿Olvidaste tu contraseña?
                </router-link>
              </div>

              <div class="text-center mt-4">
                <argon-button
                  type="submit"
                  class="btn-login-primary"
                  color="dark"
                  fullWidth
                  size="lg"
                >
                  Iniciar sesión
                </argon-button>
              </div>

              <div class="text-center mt-3" v-if="installPrompt">
                <button
                  type="button"
                  class="btn btn-install-app"
                  @click="installApp"
                >
                  <i class="fas fa-download me-2"></i>
                  Instalar aplicación
                </button>
              </div>
            </form>

            <div class="login-footer">
              <p>© 2026 MI STOCK · Controla, gestiona, crece.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import formMixin from "../../mixins/form-mixin.js"
import ValidationError from "../../components/ValidationError.vue";
import showSwal from "../../mixins/showSwal.js";
import Swal from 'sweetalert2';

const body = document.getElementsByTagName("body")[0];

export default {
  name: "Login",
  components: {
    ArgonInput,
    ArgonSwitch,
    ArgonButton,
    ValidationError
  },
  mixins: [formMixin, showSwal],
  data() {
    return {
      user: {
        email: '',
        password: ''
      },
      currentImage: 0,
      installPrompt: null,
      carouselImages: [
        { src: '/assets/fashion-image-1.png', alt: 'Moda Elegante' },
        { src: '/assets/fashion-image-2.png', alt: 'Estilo Urbano' },
        { src: '/assets/fashion-image-3.png', alt: 'Accesorios de Moda' },
      ]
    }
  },
  created() {
    this.$store.state.hideConfigButton = true;
    this.$store.state.showNavbar = false;
    this.$store.state.showSidenav = false;
    this.$store.state.showFooter = false;
    body.classList.remove("bg-gray-100");
  },
  beforeUnmount() {
    this.$store.state.hideConfigButton = false;
    this.$store.state.showNavbar = true;
    this.$store.state.showSidenav = true;
    this.$store.state.showFooter = true;
    body.classList.add("bg-gray-100");
  },
  mounted() {
    setInterval(() => {
      this.currentImage = (this.currentImage + 1) % this.carouselImages.length;
    }, 5000);

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.installPrompt = e;
    });

    window.addEventListener('appinstalled', () => {
      this.installPrompt = null;
    });
  },
  methods: {
    async installApp() {
      if (!this.installPrompt) return;
      this.installPrompt.prompt();
      const { outcome } = await this.installPrompt.userChoice;
      if (outcome === 'accepted') {
        this.installPrompt = null;
      }
    },
    async handleLogin() {
      try {
        this.resetApiValidation();
        await this.$store.dispatch("auth/login", this.user);
        
        // El servicio guarda el usuario en localStorage de forma síncrona
        // Leer el usuario del localStorage para determinar la redirección
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          throw new Error('Usuario no encontrado después del login');
        }
        
        const user = JSON.parse(userStr);
        
        // Redirigir según el rol del usuario
        if (user && user.role === '3') {
          // Si es operador, redirigir a su dashboard
          this.$router.push({ name: "Operador Dashboard" });
        } else {
          // Si no es operador, redirigir al dashboard normal
          this.$router.push({ name: "Dashboard" });
        }

      }
      catch(error) {
        await Swal.fire({
          icon: 'error',
          title: 'Error de credenciales',
          text: error.response?.data?.message || 'Credenciales no combinan. Intenta de nuevo.',
        });
        if (error.response?.data?.errors) {
          this.setApiValidation(error.response.data.errors);
        }
      }
    },
  }
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  background-color: #ffffff;
}

.login-wrapper {
  display: flex;
  width: 100%;
  min-height: 100vh;
}

/* Left Panel */
.login-panel-left {
  width: 50%;
  background: linear-gradient(160deg, #2C3A59 0%, #16243F 60%, #0f192d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 3rem;
}

.login-mesh-bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(at 15% 25%, rgba(244, 180, 0, 0.12) 0%, transparent 40%),
    radial-gradient(at 85% 75%, rgba(44, 58, 89, 0.8) 0%, transparent 45%),
    radial-gradient(at 50% 50%, rgba(255, 255, 255, 0.03) 0%, transparent 60%);
  animation: meshMove 20s ease-in-out infinite alternate;
  pointer-events: none;
}

@keyframes meshMove {
  0% {
    transform: scale(1) translate(0, 0);
  }
  100% {
    transform: scale(1.15) translate(-3%, -3%);
  }
}

.login-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.particle {
  position: absolute;
  bottom: -20px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(244, 180, 0, 0.25);
  box-shadow: 0 0 12px rgba(244, 180, 0, 0.3);
  animation: floatUp linear infinite;
}

.particle-1 { left: 5%; animation-duration: 12s; animation-delay: 0s; width: 6px; height: 6px; }
.particle-2 { left: 12%; animation-duration: 16s; animation-delay: 2s; width: 10px; height: 10px; background: rgba(255, 255, 255, 0.15); }
.particle-3 { left: 22%; animation-duration: 14s; animation-delay: 4s; width: 5px; height: 5px; }
.particle-4 { left: 30%; animation-duration: 18s; animation-delay: 1s; width: 9px; height: 9px; background: rgba(244, 180, 0, 0.2); }
.particle-5 { left: 42%; animation-duration: 13s; animation-delay: 5s; width: 7px; height: 7px; }
.particle-6 { left: 55%; animation-duration: 17s; animation-delay: 3s; width: 11px; height: 11px; background: rgba(255, 255, 255, 0.12); }
.particle-7 { left: 65%; animation-duration: 15s; animation-delay: 6s; width: 6px; height: 6px; }
.particle-8 { left: 75%; animation-duration: 19s; animation-delay: 2.5s; width: 8px; height: 8px; }
.particle-9 { left: 82%; animation-duration: 11s; animation-delay: 7s; width: 5px; height: 5px; background: rgba(244, 180, 0, 0.3); }
.particle-10 { left: 90%; animation-duration: 14s; animation-delay: 4.5s; width: 10px; height: 10px; }
.particle-11 { left: 18%; animation-duration: 20s; animation-delay: 8s; width: 7px; height: 7px; background: rgba(255, 255, 255, 0.1); }
.particle-12 { left: 68%; animation-duration: 12s; animation-delay: 9s; width: 6px; height: 6px; }

@keyframes floatUp {
  0% {
    transform: translateY(0) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 0.6;
  }
  100% {
    transform: translateY(-110vh) scale(0.6);
    opacity: 0;
  }
}

.login-panel-left::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.04) 0%, transparent 50%);
  pointer-events: none;
}

.login-panel-left-content {
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;
}

.login-logo-left {
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  z-index: 2;
}

.login-logo-left img {
  max-height: 60px;
  width: auto;
  filter: brightness(0) invert(1);
}

.login-illustration {
  width: 100%;
  max-width: 420px;
  aspect-ratio: 1 / 1;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 32px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 30px 60px rgba(0, 0, 0, 0.25);
}

.illustration-carousel {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.illustration-slide {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.illustration-slide img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.25));
}

.login-carousel-dots {
  display: flex;
  gap: 10px;
  margin-bottom: 2rem;
}

.login-carousel-dots span {
  width: 32px;
  height: 4px;
  border-radius: 2px;
  background-color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.login-carousel-dots span.active {
  background-color: #F4B400;
  width: 48px;
}

.login-carousel-dots span:hover {
  background-color: rgba(255, 255, 255, 0.6);
}

.login-tagline {
  color: rgba(255, 255, 255, 0.75);
  font-size: 1.05rem;
  max-width: 360px;
  line-height: 1.6;
}

.login-tagline p {
  margin: 0;
}

/* Right Panel */
.login-panel-right {
  width: 50%;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  position: relative;
  overflow: hidden;
}

.login-panel-right::before {
  content: '';
  position: absolute;
  top: -30%;
  right: -20%;
  width: 60%;
  height: 60%;
  background: radial-gradient(circle, rgba(244, 180, 0, 0.06) 0%, transparent 60%);
  pointer-events: none;
}

.login-form-card {
  width: 100%;
  max-width: 460px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 28px;
  padding: 3rem 2.5rem;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 20px 40px -10px rgba(22, 36, 63, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.5) inset;
  position: relative;
  animation: cardFloat 6s ease-in-out infinite;
  overflow: hidden;
}

.login-form-card-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(
    from 0deg,
    transparent 0deg,
    rgba(244, 180, 0, 0.08) 60deg,
    transparent 120deg,
    rgba(44, 58, 89, 0.06) 180deg,
    transparent 240deg,
    rgba(244, 180, 0, 0.08) 300deg,
    transparent 360deg
  );
  animation: glowRotate 15s linear infinite;
  pointer-events: none;
}

@keyframes glowRotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes cardFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}

.login-form-wrapper {
  width: 100%;
  position: relative;
  z-index: 1;
}

.login-form-header {
  margin-bottom: 2.5rem;
}

.login-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.login-subtitle {
  color: #64748b;
  font-size: 1rem;
  margin: 0;
}

.login-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.5rem;
}

.login-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.login-switch-input {
  width: 2.5rem;
  height: 1.3rem;
  cursor: pointer;
  background-color: #cbd5e1;
  border-color: #cbd5e1;
}

.login-switch-input:checked {
  background-color: #F4B400;
  border-color: #F4B400;
}

.login-switch-label {
  color: #475569;
  font-size: 0.9rem;
  margin-left: 0.5rem;
  cursor: pointer;
}

.login-forgot {
  color: #4a0e4e;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;
}

.login-forgot:hover {
  color: #7b1fa2;
  text-decoration: underline;
}

.btn-login-primary {
  background: linear-gradient(45deg, #16243F, #2C3A59) !important;
  border: none !important;
  color: #ffffff !important;
  font-weight: 600;
  letter-spacing: 0.3px;
  border-radius: 12px;
  padding: 0.85rem 1.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(22, 36, 63, 0.25);
  position: relative;
  overflow: hidden;
}

.btn-login-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent);
  transition: left 0.5s ease;
}

.btn-login-primary:hover::before {
  left: 100%;
}

.btn-login-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(22, 36, 63, 0.35);
  filter: brightness(1.05);
}

.btn-login-primary:active {
  transform: translateY(0);
}

.btn-install-app {
  width: 100%;
  background: transparent;
  border: 2px solid #16243F;
  color: #16243F;
  font-weight: 600;
  border-radius: 12px;
  padding: 0.65rem 1.25rem;
  transition: all 0.3s ease;
}

.btn-install-app:hover {
  background: #16243F;
  color: #ffffff;
  transform: translateY(-1px);
}

.btn-install-app i {
  font-size: 0.95rem;
}

.login-footer {
  margin-top: 3rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.85rem;
}

.login-footer p {
  margin: 0;
}

/* Input styling */
.argon-input :deep(.input-group) {
  border-radius: 12px;
  overflow: hidden;
  background-color: #f8fafc;
  box-shadow: inset 0 0 0 1.5px #e2e8f0;
  transition: all 0.2s ease;
}

.argon-input :deep(.input-group:focus-within) {
  background-color: #ffffff;
  box-shadow: inset 0 0 0 1.5px #F4B400, 0 0 0 3px rgba(244, 180, 0, 0.15);
}

.argon-input :deep(.input-group-text) {
  background: transparent !important;
  border: none !important;
  color: #94a3b8;
  padding: 0.85rem 1rem;
  border-radius: 0 !important;
  transition: all 0.2s ease;
}

.argon-input :deep(.input-group-text .fa-eye),
.argon-input :deep(.input-group-text .fa-eye-slash) {
  color: #94a3b8;
  cursor: pointer;
}

.argon-input :deep(.input-group:focus-within) .input-group-text {
  color: #F4B400;
}

.argon-input :deep(input) {
  border: none !important;
  background: transparent !important;
  border-radius: 0 !important;
  padding: 0.85rem 1rem;
  font-size: 0.95rem;
  color: #334155;
  box-shadow: none !important;
  transition: all 0.2s ease;
}

.argon-input :deep(input:focus) {
  background-color: #ffffff;
  box-shadow: none !important;
}

.argon-input :deep(.input-group > :not(:first-child):not(.dropdown-menu):not(.valid-tooltip):not(.valid-feedback):not(.invalid-tooltip):not(.invalid-feedback)) {
  border-left: none !important;
  border-right: none !important;
}

/* Fade transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.6s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 991.98px) {
  .login-panel-left {
    display: none;
  }

  .login-panel-right {
    width: 100%;
    padding: 2rem;
  }

  .login-form-card {
    padding: 2.5rem 2rem;
  }

  .login-form-wrapper {
    max-width: 420px;
  }

  .login-title {
    font-size: 1.75rem;
  }
}

@media (max-width: 575.98px) {
  .login-page,
  .login-wrapper {
    min-height: 100vh;
    height: auto;
  }

  .login-panel-right {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }

  .login-form-card {
    border-radius: 20px;
    padding: 2rem 1.5rem;
    animation: none;
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.05),
      0 20px 40px -10px rgba(22, 36, 63, 0.12);
  }

  .login-form-card-glow {
    animation: none;
  }

  .login-form-wrapper {
    max-width: 100%;
  }

  .login-form-header {
    margin-bottom: 2rem;
  }

  .login-title {
    font-size: 1.5rem;
  }

  .login-subtitle {
    font-size: 0.95rem;
  }

  .login-options {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }

  .login-footer {
    margin-top: 2rem;
  }
}
</style>

